import { ApiQueryOptions, isRequireBody } from '@src/types/ApiQuery';
import { store } from '@store';
import { saveAs } from 'file-saver';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { API_URL, API_V2_URL } from '@utils';

export const getAPIConfig = (path: string) => {
  const bearerToken = store.getState().user.keycloak?.token;
  return { url: `${API_URL}${path}`, bearerToken };
};
export const getV2APIConfig = (path: string) => {
  const bearerToken = store.getState().user.keycloak?.token;
  return { url: `${API_V2_URL}${path}`, bearerToken };
};

export const queryFn = async (opts: ApiQueryOptions) => {
  const apiConfig = opts.useV2API
    ? getV2APIConfig(opts.apiPath)
    : getAPIConfig(opts.apiPath);
  const apiResponse = await fetch(apiConfig.url, {
    method: opts.method,
    headers: {
      authorization: `Bearer ${apiConfig.bearerToken}`,
      'Content-Type': 'application/json',
      ...opts.headers,
    },
    ...(isRequireBody(opts) ? { body: JSON.stringify(opts.body) } : {}),
  });

  if (!apiResponse.ok) {
    throw new Error(`Failed to fetch from ${apiResponse.url}`, {
      cause: apiResponse,
    });
  }

  if (opts.isFile) {
    const contentType = apiResponse.headers.get('Content-Type');
    const contentDisposition = apiResponse.headers.get('Content-Disposition');
    const blob = await apiResponse.blob();

    const extension = contentType?.includes('csv') ? 'csv' : 'json';

    let fileName = 'download';

    if (opts.customFilename) {
      fileName = opts.customFilename;
    } else if (contentDisposition) {
      const matches = contentDisposition.match(
        /filename[^=*=]*=((['"]).*?\2|([^;\n]*))/,
      );
      if (matches && matches[3]) {
        fileName = matches[3].replace(/['"]/g, '');
      }
    }

    if (!fileName.includes('.')) {
      fileName += `.${extension}`;
    } else if (!fileName.endsWith(`.${extension}`)) {
      fileName =
        fileName.substring(0, fileName.lastIndexOf('.')) + `.${extension}`;
    }

    saveAs(blob, fileName);
    return apiResponse;
  }

  return apiResponse.json();
};

const useApiQuery = <T, TSelected = T>(
  opts: ApiQueryOptions,
  queryOptions?: Partial<UseQueryOptions<T, Error, TSelected>>,
) =>
  useQuery<T, Error, TSelected>({
    queryKey: opts.queryKey,
    queryFn: () => queryFn(opts),
    enabled: opts.enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    select: opts.select as ((data: T) => TSelected) | undefined,
    ...queryOptions,
  });

export default useApiQuery;
